import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  CloseButton,
  Box,
  Button,
  Center,
  Drawer,
  Flex,
  Heading,
  HStack,
  IconButton,
  Input,
  Spinner,
  Switch,
  Text,
  VStack,
  Dialog,
  Portal,
  Separator,
  Field,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { Tooltip } from '@/components/ui/tooltip';
import { FiCopy, FiList, FiMapPin, FiPlus, FiTrash2 } from 'react-icons/fi'
import { PageContainer } from '../components/shared'
import { ContestPicker } from '../components/electionSim/ContestPicker'
import { LeverPanel } from '../components/electionSim/LeverPanel'
import type { Levers } from '../components/electionSim/LeverPanel'
import { OutcomeCard } from '../components/electionSim/OutcomeCard'
import { CompareStrip } from '../components/electionSim/CompareStrip'
import { TornadoCard } from '../components/electionSim/TornadoCard'
import { SensitivityMapPane } from '../components/electionSim/SensitivityMapPane'
import { SensitivityList } from '../components/electionSim/SensitivityList'
import { ChamberOutcome } from '../components/electionSim/ChamberOutcome'
import { SeatBoard } from '../components/electionSim/SeatBoard'
import { AllocationTable } from '../components/electionSim/AllocationTable'
import { serializeLevers, parseLevers } from '../components/electionSim/scenarioLevers'
import { positiveNetPrecinctIds } from '../components/electionSim/targetingPresets'
import { explainCopy } from '../lib/explainCopy'
import { extractApiMessage } from '../lib/apiError'
import { convexHullWkt } from '../components/electionSim/geometry'
import { CreateTurfModal } from '../components/analyticsMap/CreateTurfModal'
import { useIsMobile } from '../hooks/useIsMobile'
import {
  useScenarios,
  useScenario,
  useSaveScenario,
  useDeleteScenario,
  useSimRun,
  useSimBaseline,
  usePinScenario,
} from '../hooks/useElectionSim'
import { useGeoUnitProfile, useGeoUnits } from '../hooks/useGeoAnalytics'
import { useExportToList } from '../hooks/useAnalyticsMap'
import { CAPACITY_DEFAULTS, ASSUMPTION_DEFAULTS, SMART_FILTER_DEFAULTS, PRIMARY_SMART_FILTER_DEFAULTS } from '../types/electionSim'
import type { ContestRequest, ScenarioSummary, SimRunRequest, SimRunResponse } from '../types/electionSim'
import type { GeoUnitTypeKey } from '../types/geoAnalytics'
import { useUsabilityMark } from '../tools/usability/usabilityMark'
import { contestMarkDetail, runMarkDetail } from '../components/electionSim/scenarioMarks'
import '../components/districtProfile/print.css'

// Monte Carlo draw count — a fixed model constant (backend ElectionSimOptions.Draws default),
// surfaced to the chamber majority-probability tooltip so it reads honestly (mirrors OutcomeCard).
const SIM_DRAWS = 1000

const DEFAULT_CONTEST: ContestRequest = {
  type: 'district',
  office: 'StateHouse',
  state: '',
  districtGeoUnitId: null,
  raceKind: 'general',
}

const DEFAULT_LEVERS: Levers = {
  capacity: CAPACITY_DEFAULTS,
  targeting: { kind: 'geography' },
  assumptions: ASSUMPTION_DEFAULTS,
  advanced: null,
}

/** District geo-unit type -> the ElectionOffice enum name the sim API expects. Mirrors
 * WhatIfSnapshotCard's OFFICE_BY_UNIT so a `?district=` deep-link (from that card's
 * "Simulate this race" button) resolves to the same office this district would use there. */
const OFFICE_BY_UNIT_TYPE: Partial<Record<GeoUnitTypeKey, string>> = {
  stateHouseDistrict: 'StateHouse',
  stateSenateDistrict: 'StateSenate',
  congressionalDistrict: 'USHouse',
}

/** Human-readable one-liner for a scenario's persisted contest JSON, used by both the desktop
 * list rows and the mobile read-only list. Tolerant of malformed/legacy JSON. */
function summarizeContest(contestJson: string): string {
  try {
    const c = JSON.parse(contestJson) as Partial<ContestRequest>
    if (!c?.type) return 'Unknown contest'
    if (c.type === 'chamber') {
      const chamberLabel = c.chamber === 'stateSenate' ? 'State Senate' : 'State House'
      return `Chamber control · ${chamberLabel} · ${c.state ?? '—'}`
    }
    if (c.type === 'statewide') {
      return `Statewide · ${c.office ?? '—'} · ${c.state ?? '—'}`
    }
    return `District · ${c.office ?? '—'} · ${c.state ?? '—'}`
  } catch {
    return 'Unknown contest'
  }
}

export function ScenarioWorkspacePage() {
  const isMobile = useIsMobile()
  const [searchParams] = useSearchParams()
  const districtParam = searchParams.get('district')
  const cancelRef = useRef<HTMLButtonElement>(null)

  const [loadedId, setLoadedId] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [contest, setContest] = useState<ContestRequest>(DEFAULT_CONTEST)
  const [seededFromUrl, setSeededFromUrl] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<ScenarioSummary | null>(null)
  const [levers, setLevers] = useState<Levers>(DEFAULT_LEVERS)
  // Reachable-cohort count + full run response come from the sim (Task 18 renders the results).
  const [reachable, setReachable] = useState<number | null>(null)
  const [run, setRun] = useState<SimRunResponse | null>(null)
  // Chamber effort allocation (seatCode -> shifts), threaded into SimRunRequest.seatShiftAllocation.
  const [allocation, setAllocation] = useState<Record<string, number>>({})
  // Polygon-scoped turf modal payload; open when non-null ("Create turf from top 5").
  const [turfModal, setTurfModal] = useState<{ polygonWkt: string; label: string } | null>(null)
  // Scenario rail collapses to a slim reopen strip once a non-chamber run exists (Task 8),
  // freeing full width for the sensitivity map + tornado below.
  const [railCollapsed, setRailCollapsed] = useState(false)
  // "Compare to untargeted GOTV" toggle: when on, a single-contest run also resolves the whole-district
  // cohort and returns the delta strip. Chamber runs never compare.
  const [compareUntargeted, setCompareUntargeted] = useState(false)
  // Mobile-only: bottom Drawer for scenario management (new/duplicate/delete/switch).
  const [scenarioSheetOpen, setScenarioSheetOpen] = useState(false)

  const scenariosQuery = useScenarios()
  const scenarioQuery = useScenario(loadedId)
  const saveScenario = useSaveScenario()
  const deleteScenario = useDeleteScenario()
  const pinScenario = usePinScenario()
  const simRun = useSimRun()
  const exportToList = useExportToList()

  // Baseline (for the outcome margin band) + precinct polygons (for the top-5 convex hull) are
  // only needed once a run exists; the precinct query shares SensitivityMapPane's key so it dedupes.
  // No vintage: the /geo/units endpoint resolves MAX(VintageYear) for precinct+state server-side.
  const baselineQuery = useSimBaseline(run ? contest : null)
  const precinctsQuery = useGeoUnits(
    run && contest.state ? { type: 'precinct', state: contest.state } : null,
  )

  // Total shift budget for the current capacity plan (canvassers × shifts/week × weeks); the
  // chamber allocation table balances the per-seat shifts against this ceiling.
  const totalShifts = levers.capacity.canvassers * levers.capacity.shiftsPerWeek * levers.capacity.weeks

  // Only fetch the unit profile while we still need it to resolve the ?district= deep-link.
  const geoUnitProfileQuery = useGeoUnitProfile(districtParam && !seededFromUrl ? districtParam : null)

  // True only while a ?district= deep-link is still resolving to its full contest (office + state).
  // Until the profile loads the office is an unknown default (StateHouse) and the state is empty —
  // rendering the picker or emitting a contest mark during this window flashes the wrong race, so
  // we hold both. A profile-fetch error counts as "resolved" so we fall back to the picker instead
  // of spinning forever, and loading a saved scenario overrides the deep-link entirely.
  const resolvingDeepLink =
    !!districtParam && !seededFromUrl && !loadedId && !geoUnitProfileQuery.isError

  function doRun(compare: boolean) {
    const request: SimRunRequest = {
      contest,
      capacity: levers.capacity,
      targeting: levers.targeting,
      assumptions: levers.assumptions,
      advanced: levers.advanced,
      seatShiftAllocation: contest.type === 'chamber' ? allocation : undefined,
      compareUntargeted: compare,
    }
    simRun.mutate(request, {
      onSuccess: (res) => {
        setReachable(res.totalReachableVoters)
        setRun(res)
      },
      onError: (error) =>
        toaster.create({
          title: 'Run failed',
          description: extractApiMessage(error, 'An error occurred while running the simulation.'),
          type: 'error',
          duration: 5000,
        }),
    })
  }

  // Arg-less so it is safe as an onClick handler (Chakra passes the click event to onRun).
  function handleRun() {
    doRun(compareUntargeted && contest.type !== 'chamber')
  }

  function handleContestChange(next: ContestRequest) {
    // When the race kind flips, reseed an active smart filter to that mode's defaults so a
    // general filter's values (e.g. propensity 30–60) don't linger into primary mode.
    const kindChanged = (next.raceKind ?? 'general') !== (contest.raceKind ?? 'general')
    if (kindChanged && levers.targeting.kind === 'smartfilter') {
      const smartFilter = next.raceKind === 'primary' ? PRIMARY_SMART_FILTER_DEFAULTS : SMART_FILTER_DEFAULTS
      setLevers((l) => ({ ...l, targeting: { ...l.targeting, smartFilter } }))
    }
    setContest(next)
  }

  const contestLabel = summarizeContest(JSON.stringify(contest))

  // Usability capture: annotate contest selection + each run. No-op unless a
  // ?usability session is recording. See frontend/src/tools/usability/.
  const usabilityMark = useUsabilityMark()
  useEffect(() => {
    // Hold the contest mark until a ?district= deep-link resolves — otherwise the interim
    // default-office (StateHouse) state emits a spurious mark before the real race is known.
    if (resolvingDeepLink) return
    usabilityMark('contest', contestMarkDetail(contest))
  }, [contest, usabilityMark, resolvingDeepLink])
  useEffect(() => {
    if (!run) return
    // Reads current contest/levers/allocation via closure at run time (deliberate — a
    // run mark should reflect what produced THIS run, and re-runs always set a new `run`).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    usabilityMark('run', runMarkDetail(contest, levers, allocation, run))
  }, [run, usabilityMark])
  // Auto-collapse the scenario rail once a non-chamber run exists, giving the results
  // (OutcomeCard, full-width map, tornado) the room they need. Chamber runs keep the rail
  // open since the allocation table lives beside the seat board rather than a map row.
  useEffect(() => {
    if (!isMobile && run && contest.type !== 'chamber') setRailCollapsed(true)
  }, [run, contest.type, isMobile])

  function handleAllocationChange(seatCode: string, shifts: number) {
    setAllocation((a) => ({ ...a, [seatCode]: Math.max(0, shifts) }))
  }

  // NOTE: greedy auto-suggest is deferred pending a backend per-seat capacity field. The pure
  // `greedyAllocate` mirror + its unit tests are retained in AllocationTable.tsx and will be
  // re-wired once the backend exposes max-useful-shifts per seat; the button renders disabled.

  /** Convex hull (WKT) of the precinct polygons for the given tornado geo-unit ids, or '' if the
   *  precinct geometry hasn't loaded / none match. */
  function hullForUnits(geoUnitIds: string[]): string {
    const features = (precinctsQuery.data?.features ?? []).filter((f) =>
      geoUnitIds.includes(f.properties?.id as string),
    )
    return convexHullWkt(features)
  }

  function handleCreateTurfFromTop5(geoUnitIds: string[]) {
    const wkt = hullForUnits(geoUnitIds)
    if (!wkt) {
      toaster.create({
        title: 'Precinct geometry not ready',
        description: 'Precinct polygons are still loading — try again in a moment.',
        type: 'warning',
        duration: 4000,
      })
      return
    }
    setTurfModal({ polygonWkt: wkt, label: `Sim targets — ${contestLabel}` })
  }

  function handleExportCohort(geoUnitIds: string[]) {
    const wkt = hullForUnits(geoUnitIds)
    if (!wkt) {
      toaster.create({
        title: 'Precinct geometry not ready',
        description: 'Precinct polygons are still loading — try again in a moment.',
        type: 'warning',
        duration: 4000,
      })
      return
    }
    exportToList.mutate(
      { filterGroups: [], polygonWkt: wkt, label: `Sim targets — ${contestLabel}` },
      {
        onSuccess: () => toaster.create({ title: 'Cohort exported to a list', type: 'success', duration: 3000 }),
        onError: (err) =>
          toaster.create({
            title: 'Export failed',
            description: extractApiMessage(err, 'Could not export this cohort to a list.'),
            type: 'error',
            duration: 5000,
          }),
      },
    )
  }

  async function handleSaveAndPin() {
    if (!name.trim()) {
      toaster.create({
        title: 'Name required',
        description: 'Give this scenario a name before pinning it.',
        type: 'warning',
        duration: 4000,
      })
      return
    }
    if (!contest.districtGeoUnitId) return
    try {
      const saved = await saveScenario.mutateAsync({
        id: loadedId,
        data: { name: name.trim(), contestJson: JSON.stringify(contest), leversJson: serializeLevers(levers) },
      })
      setLoadedId(saved.id)
      await pinScenario.mutateAsync({ id: saved.id, geoUnitId: contest.districtGeoUnitId })
      toaster.create({ title: 'Scenario saved & pinned to district profile', type: 'success', duration: 3000 })
    } catch (err) {
      toaster.create({
        title: 'Save & pin failed',
        description: extractApiMessage(err, 'An error occurred while pinning the scenario.'),
        type: 'error',
        duration: 5000,
      })
    }
  }
  // ?district=<geoUnitId> deep-link (from WhatIfSnapshotCard's "Simulate this race" button):
  // pin the district id immediately so an error fallback still lands on this district; the picker
  // stays gated (resolvingDeepLink) and office + state fill in once the unit profile loads below.
  useEffect(() => {
    if (districtParam && !seededFromUrl && !loadedId) {
      setContest((c) => ({ ...c, type: 'district', districtGeoUnitId: districtParam }))
    }
  }, [districtParam, seededFromUrl, loadedId])

  useEffect(() => {
    if (districtParam && !seededFromUrl && geoUnitProfileQuery.data) {
      const office = OFFICE_BY_UNIT_TYPE[geoUnitProfileQuery.data.unitType] ?? 'StateHouse'
      setContest({
        type: 'district',
        office,
        state: geoUnitProfileQuery.data.state,
        districtGeoUnitId: districtParam,
      })
      setSeededFromUrl(true)
    }
  }, [districtParam, seededFromUrl, geoUnitProfileQuery.data])

  // Loading a saved scenario populates the name + contest + levers from its persisted JSON.
  useEffect(() => {
    if (scenarioQuery.data) {
      setName(scenarioQuery.data.name)
      try {
        setContest(JSON.parse(scenarioQuery.data.contestJson) as ContestRequest)
      } catch {
        // Malformed persisted JSON shouldn't crash the picker — leave whatever is showing.
      }
      // parseLevers is defensive: legacy '{}' / malformed / partial payloads fall back to defaults.
      setLevers(parseLevers(scenarioQuery.data.leversJson, DEFAULT_LEVERS))
    }
  }, [scenarioQuery.data])

  function handleNewScenario() {
    setLoadedId(null)
    setName('')
    setContest(DEFAULT_CONTEST)
  }

  function handleDuplicate() {
    if (!loadedId) return
    setLoadedId(null)
    setName((n) => (n ? `${n} (copy)` : n))
  }

  async function handleSave() {
    if (!name.trim()) {
      toaster.create({
        title: 'Name required',
        description: 'Give this scenario a name before saving.',
        type: 'warning',
        duration: 4000,
      })
      return
    }
    try {
      const saved = await saveScenario.mutateAsync({
        id: loadedId,
        data: { name: name.trim(), contestJson: JSON.stringify(contest), leversJson: serializeLevers(levers) },
      })
      setLoadedId(saved.id)
      toaster.create({ title: 'Scenario saved', type: 'success', duration: 3000 })
    } catch (err) {
      toaster.create({
        title: 'Save failed',
        description: extractApiMessage(err, 'An error occurred while saving the scenario.'),
        type: 'error',
        duration: 5000,
      })
    }
  }

  async function handleConfirmDelete() {
    if (!deleteTarget) return
    try {
      await deleteScenario.mutateAsync(deleteTarget.id)
      if (loadedId === deleteTarget.id) handleNewScenario()
      toaster.create({ title: 'Scenario deleted', type: 'success', duration: 3000 })
    } catch (err) {
      toaster.create({
        title: 'Delete failed',
        description: extractApiMessage(err, 'An error occurred while deleting the scenario.'),
        type: 'error',
        duration: 5000,
      })
    } finally {
      setDeleteTarget(null)
    }
  }

  // Run-dependent results, shared by the desktop and mobile branches. Chamber vs. single-contest
  // mirrors the desktop layout exactly; only the sensitivity view forks (map on desktop, static
  // list on mobile so MapLibre never loads on a phone).
  function renderResults() {
    if (!run) return null
    if (contest.type === 'chamber') {
      if (!run.chamber && run.coverage.basis !== 'topOfTicket') return null
      return (
        <>
          <ChamberOutcome run={run} draws={SIM_DRAWS} />
          {run.chamber && (
            <>
              <SeatBoard seats={run.chamber.seats ?? []} />
              <AllocationTable
                seats={run.chamber.seats ?? []}
                allocation={allocation}
                totalShifts={totalShifts}
                onChange={handleAllocationChange}
                onRun={handleRun}
                isRunning={simRun.isPending}
              />
            </>
          )}
        </>
      )
    }
    return (
      <>
        <Field.Root display="flex" alignItems="center" justifyContent="flex-end">
          <Tooltip content={explainCopy.compareToUntargeted()} openDelay={0} showArrow contentProps={{ maxW: "320px" }} positioning={{
            placement: "top"
          }}>
            <Field.Label
              htmlFor="compare-untargeted"
              mb={0}
              fontSize="sm"
              color="gray.600"
              cursor="help"
              tabIndex={0}
              width="fit-content"
              borderBottomWidth="1px"
              borderBottomStyle="dashed"
              borderBottomColor="accent"
            >
              Compare to untargeted GOTV
            </Field.Label>
          </Tooltip>
          <Switch.Root
            id="compare-untargeted"
            colorPalette="brand"
            checked={compareUntargeted}
            onCheckedChange={(e) => {
              const next = e.checked === true
              setCompareUntargeted(next)
              doRun(next && contest.type !== 'chamber')
            }}
          >
            <Switch.HiddenInput />
            <Switch.Control>
              <Switch.Thumb />
            </Switch.Control>
          </Switch.Root>
        </Field.Root>
        <CompareStrip run={run} />
        <OutcomeCard run={run} baseline={baselineQuery.data} />
        <Box data-testid="sensitivity-map-row">
          {isMobile ? (
            <SensitivityList
              sensitivities={run.unitSensitivities ?? []}
              features={precinctsQuery.data?.features ?? []}
            />
          ) : (
            <SensitivityMapPane state={contest.state} sensitivities={run.unitSensitivities ?? []} />
          )}
        </Box>
        <TornadoCard
          tornado={run.tornado ?? []}
          onCreateTurf={handleCreateTurfFromTop5}
          onExport={handleExportCohort}
        />
        {contest.type === 'district' && !!contest.districtGeoUnitId && (
          <Button
            colorPalette="brand"
            variant="outline"
            size="sm"
            alignSelf="flex-start"
            onClick={handleSaveAndPin}
            loading={saveScenario.isPending || pinScenario.isPending}><FiMapPin />Save & pin to district profile
                      </Button>
        )}
      </>
    );
  }

  // CreateTurfModal + delete confirmation, shared by both branches.
  function renderModals() {
    return (
      <>
        <CreateTurfModal
          isOpen={!!turfModal}
          onClose={() => setTurfModal(null)}
          scope="polygon"
          filterGroups={[]}
          polygonWkt={turfModal?.polygonWkt}
          label={turfModal?.label ?? ''}
          onExported={() => setTurfModal(null)}
        />
        <Dialog.Root
          open={!!deleteTarget}
          initialFocusEl={() => cancelRef.current}
          role='alertdialog'
          onOpenChange={e => {
            if (!e.open) {
              setDeleteTarget(null);
            }
          }}>
          <Portal>

            <Dialog.Backdrop>
              <Dialog.Positioner>
                <Dialog.Content>
                  <Dialog.Header fontSize="lg" fontWeight="bold">
                    Delete Scenario
                  </Dialog.Header>
                  <Dialog.Body>
                    Are you sure you want to delete <strong>&quot;{deleteTarget?.name}&quot;</strong>? This action
                    cannot be undone.
                  </Dialog.Body>
                  <Dialog.Footer>
                    <Button ref={cancelRef} onClick={() => setDeleteTarget(null)}>
                      Cancel
                    </Button>
                    <Button colorPalette="red" onClick={handleConfirmDelete} ml={3} loading={deleteScenario.isPending}>
                      Delete
                    </Button>
                  </Dialog.Footer>
                </Dialog.Content>
              </Dialog.Positioner>
            </Dialog.Backdrop>

          </Portal>
</Dialog.Root>
      </>
    );
  }

  const scenarios = scenariosQuery.data ?? []

  // ---------------------------------------------------------------------
  // Mobile: full authoring parity (layout A — one long scroll). Scenario
  // management lives in a bottom Drawer; the map is swapped for the static
  // SensitivityList via renderResults().
  // ---------------------------------------------------------------------
  if (isMobile) {
    return (
      <PageContainer title="Scenario Workspace">
        <VStack align="stretch" gap={5}>
          <Flex justify="space-between" align="center" gap={2}>
            <Box minW={0}>
              <Text fontSize="xs" color="gray.500">
                Scenario
              </Text>
              <Text fontWeight="semibold" fontSize="sm" lineClamp={1}>
                {name.trim() || 'Untitled scenario'}
              </Text>
            </Box>
            <Button
              size="sm"
              variant="outline"
              flexShrink={0}
              onClick={() => setScenarioSheetOpen(true)}><FiList />Scenarios
                          </Button>
          </Flex>

          <HStack>
            <Input
              placeholder="Scenario name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              size="sm"
            />
            <Button
              colorPalette="brand"
              size="sm"
              flexShrink={0}
              onClick={handleSave}
              loading={saveScenario.isPending}
            >
              Save
            </Button>
          </HStack>

          <Separator />

          {resolvingDeepLink ? (
            <Center py={4}>
              <Spinner size="md" color="accent" />
            </Center>
          ) : (
            <ContestPicker value={contest} onChange={handleContestChange} />
          )}

          <LeverPanel
            levers={levers}
            onChange={setLevers}
            onRun={handleRun}
            isRunning={simRun.isPending}
            reachable={reachable}
            contestForCount={contest.type === 'district' && contest.districtGeoUnitId ? contest : null}
            positiveNetPrecinctIds={positiveNetPrecinctIds(run)}
            isPrimary={contest.raceKind === 'primary'}
          />

          {renderResults()}
        </VStack>

        <Drawer.Root
          open={scenarioSheetOpen}
          placement='bottom'
          onOpenChange={e => {
            if (!e.open) {
              setScenarioSheetOpen(false);
            }
          }}
        >
          <Portal>

            <Drawer.Backdrop />
            <Drawer.Positioner>
              <Drawer.Content borderTopRadius="lg" pb="env(safe-area-inset-bottom, 0px)" maxH="80vh">
                <Drawer.CloseTrigger asChild><CloseButton size="sm" /></Drawer.CloseTrigger>
                <Drawer.Header fontSize="md">Scenarios</Drawer.Header>
                <Drawer.Body>
                  <VStack align="stretch" gap={2} mb={4}>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        handleNewScenario()
                        setScenarioSheetOpen(false)
                      }}><FiPlus />New scenario
                                      </Button>
                    <HStack>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!loadedId}
                        flex="1"
                        onClick={() => {
                          handleDuplicate()
                          setScenarioSheetOpen(false)
                        }}><FiCopy />Duplicate
                                          </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        colorPalette="red"
                        disabled={!loadedId}
                        flex="1"
                        onClick={() => {
                          const target = scenarios.find((s) => s.id === loadedId)
                          if (target) setDeleteTarget(target)
                        }}><FiTrash2 />Delete
                                          </Button>
                    </HStack>
                  </VStack>

                  {scenariosQuery.isLoading ? (
                    <Center py={4}>
                      <Spinner size="md" color="accent" />
                    </Center>
                  ) : scenarios.length === 0 ? (
                    <Text color="gray.500" fontSize="sm">
                      No saved scenarios yet.
                    </Text>
                  ) : (
                    <VStack align="stretch" gap={2} pb={2}>
                      {scenarios.map((s) => (
                        <Box
                          key={s.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => {
                            setLoadedId(s.id)
                            setScenarioSheetOpen(false)
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              setLoadedId(s.id)
                              setScenarioSheetOpen(false)
                            }
                          }}
                          p={3}
                          borderWidth="1px"
                          borderRadius="md"
                          bg={loadedId === s.id ? 'accent.soft' : 'white'}
                          borderColor={loadedId === s.id ? 'accent' : 'gray.200'}
                        >
                          <Text fontWeight="semibold" fontSize="sm">
                            {s.name}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            {summarizeContest(s.contestJson)}
                          </Text>
                          <Text fontSize="xs" color="gray.400">
                            Updated {new Date(s.updatedAt).toLocaleDateString()}
                          </Text>
                        </Box>
                      ))}
                    </VStack>
                  )}
                </Drawer.Body>
              </Drawer.Content>
            </Drawer.Positioner>

          </Portal>
        </Drawer.Root>

        {renderModals()}
      </PageContainer>
    );
  }

  // ---------------------------------------------------------------------
  // Desktop: left rail (scenario list + contest picker + save), right
  // placeholder for the levers/run pane (Tasks 17–18).
  // ---------------------------------------------------------------------
  return (
    <PageContainer title="Scenario Workspace">
      <Flex direction={{ base: 'column', lg: 'row' }} gap={6} align="flex-start">
        {/* Collapsed rail renders NO left column (a 40px icon column + the 24px gap left an empty
            gutter beside the results); the reopen control lives at the top of the main column. */}
        {!railCollapsed && (
        <Box w={{ base: 'full', lg: '360px' }} flexShrink={0} data-testid="scenario-rail">
          <VStack align="stretch" gap={4}>
            <Flex justify="space-between" align="center">
              <Heading size="sm">Scenarios</Heading>
              <IconButton aria-label="New scenario" size="sm" onClick={handleNewScenario}><FiPlus /></IconButton>
            </Flex>

            {scenariosQuery.isLoading ? (
              <Center py={4}>
                <Spinner size="md" color="accent" />
              </Center>
            ) : scenarios.length === 0 ? (
              <Text color="gray.500" fontSize="sm">
                No saved scenarios yet.
              </Text>
            ) : (
              <VStack align="stretch" gap={2}>
                {scenarios.map((s) => (
                  <Box
                    key={s.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setLoadedId(s.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') setLoadedId(s.id)
                    }}
                    p={3}
                    borderWidth="1px"
                    borderRadius="md"
                    bg={loadedId === s.id ? 'accent.soft' : 'white'}
                    borderColor={loadedId === s.id ? 'accent' : 'gray.200'}
                    cursor="pointer"
                    _hover={{ borderColor: 'accent' }}
                  >
                    <Text fontWeight="semibold" fontSize="sm">
                      {s.name}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      {summarizeContest(s.contestJson)}
                    </Text>
                    <Text fontSize="xs" color="gray.400">
                      Updated {new Date(s.updatedAt).toLocaleDateString()}
                    </Text>
                  </Box>
                ))}
              </VStack>
            )}

            <HStack flexWrap="wrap">
              <Button
                size="sm"
                variant="outline"
                disabled={!loadedId}
                onClick={handleDuplicate}><FiCopy />Duplicate
                              </Button>
              <Button
                size="sm"
                variant="outline"
                colorPalette="red"
                disabled={!loadedId}
                onClick={() => {
                  const target = scenarios.find((s) => s.id === loadedId)
                  if (target) setDeleteTarget(target)
                }}><FiTrash2 />Delete
                              </Button>
            </HStack>

            <Separator />

            {resolvingDeepLink ? (
              <Center py={4}>
                <Spinner size="md" color="accent" />
              </Center>
            ) : (
              <ContestPicker value={contest} onChange={handleContestChange} />
            )}

            <Separator />

            <VStack align="stretch" gap={2}>
              <Input
                placeholder="Scenario name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                size="sm"
              />
              <Button colorPalette="brand" size="sm" onClick={handleSave} loading={saveScenario.isPending}>
                Save
              </Button>
            </VStack>
          </VStack>
        </Box>
        )}

        <Box flex="1" minW={0} w="full">
          <VStack align="stretch" gap={6}>
            {railCollapsed && (
              <Button
                aria-label="Show scenarios"
                size="sm"
                variant="outline"
                alignSelf="flex-start"
                onClick={() => setRailCollapsed(false)}
              >
                <FiList />Show scenarios
              </Button>
            )}
            <LeverPanel
              levers={levers}
              onChange={setLevers}
              onRun={handleRun}
              isRunning={simRun.isPending}
              reachable={reachable}
              contestForCount={contest.type === 'district' && contest.districtGeoUnitId ? contest : null}
              positiveNetPrecinctIds={positiveNetPrecinctIds(run)}
              isPrimary={contest.raceKind === 'primary'}
            />
            {renderResults()}
          </VStack>
        </Box>
      </Flex>

      {renderModals()}
    </PageContainer>
  );
}
